import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Truck, Bike, Train, Bus, Package, XCircle, Loader2, Wallet } from "lucide-react";
import { sizeLabel } from "@/lib/sizes";
import ContactButtons from "@/components/ContactButtons";

const ModeIcon = ({ m }) => {
  const map = { car: Truck, bike: Bike, train: Train, bus: Bus };
  const Icon = map[m] || Truck;
  return <Icon className="w-3.5 h-3.5"/>;
};

const STATUS_STYLE = {
  requested: "bg-slate-100 text-slate-700",
  accepted: "bg-blue-100 text-blue-700",
  in_transit: "bg-amber-100 text-amber-800",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-700",
};

const MyTrips = () => {
  const [trips, setTrips] = useState([]);
  const [open, setOpen] = useState({});
  const [cancelling, setCancelling] = useState(null);   // trip object being cancelled
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [showEarnings, setShowEarnings] = useState(false);

  const load = () => api.get("/trips/mine").then(r => setTrips(r.data));
  const loadEarnings = () => api.get("/users/me/earnings").then(r => setEarnings(r.data)).catch(() => setEarnings(null));
  useEffect(() => { load(); loadEarnings(); }, []);

  const canCancel = (t) => {
    if (!t.active || t.cancelled) return false;
    // Any in-transit delivery blocks cancellation (must dispute instead)
    return !(t.deliveries || []).some(d => ["in_transit", "picked_up", "delivered"].includes(d.status));
  };

  const submitCancel = async () => {
    if (!cancelling) return;
    const r = (reason || "").trim();
    if (!r) { toast.error("Please give a short reason."); return; }
    setBusy(true);
    try {
      const res = await api.post(`/trips/${cancelling.id}/cancel`, { reason: r });
      const n = res.data?.affected_parcels || 0;
      toast.success(n > 0
        ? `Trip cancelled. ${n} sender${n > 1 ? "s" : ""} notified and refunded.`
        : "Trip cancelled.");
      setCancelling(null); setReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel this trip.");
    } finally { setBusy(false); }
  };

  if (!trips.length) return (
    <div className="space-y-3">
      {earnings && (
        <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid="carrier-earnings-card">
          <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5"/> Earnings</div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <span className="text-2xl font-display font-bold tabular-nums" data-testid="earn-total">₹{(earnings.total_earned || 0).toFixed(0)}</span>
            <span className="text-xs text-slate-500">total earned</span>
          </div>
        </div>
      )}
      <div className="rounded-3xl bg-white border p-8 text-center text-slate-500">
        <Package className="w-8 h-8 mx-auto mb-3 text-slate-300"/>
        No trips yet · publish one to start earning.
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {earnings && (
        <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid="carrier-earnings-card">
          <button className="w-full flex items-center justify-between text-left" onClick={() => setShowEarnings(v => !v)}>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5"/> Earnings</div>
              <div className="mt-1 flex items-baseline gap-3 flex-wrap">
                <span className="text-2xl font-display font-bold tabular-nums" data-testid="earn-total">₹{(earnings.total_earned || 0).toFixed(0)}</span>
                <span className="text-xs text-slate-500">total earned</span>
                <span className="ml-2 text-xs text-slate-500">·</span>
                <span className="text-xs" data-testid="earn-pending">Pending <b className="tabular-nums">₹{(earnings.pending_amount || 0).toFixed(0)}</b></span>
                <span className="text-xs" data-testid="earn-queued">In batch <b className="tabular-nums">₹{(earnings.queued_amount || 0).toFixed(0)}</b></span>
                <span className="text-xs" data-testid="earn-paid">Paid <b className="tabular-nums">₹{(earnings.paid_amount || 0).toFixed(0)}</b></span>
              </div>
            </div>
            {showEarnings ? <ChevronUp className="w-4 h-4 text-slate-400"/> : <ChevronDown className="w-4 h-4 text-slate-400"/>}
          </button>
          {showEarnings && (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <div className="text-xs text-slate-500 mb-1.5">Paid batches</div>
              {(earnings.paid_batches || []).length === 0 && (
                <div className="text-xs text-slate-400">Nothing has been paid yet. Once the admin transfers a batch, you'll see the reference and date here so you can match it to your bank statement.</div>
              )}
              <div className="space-y-2">
                {(earnings.paid_batches || []).slice(0, 8).map(b => (
                  <div key={b.payout_id} className="flex items-center justify-between text-xs">
                    <div>
                      <div className="font-mono text-slate-700">{b.reference || "—"}</div>
                      <div className="text-slate-400">{b.paid_at ? new Date(b.paid_at).toLocaleDateString() : ""} · {b.count} deliveries · {(b.method || "").toUpperCase()}</div>
                    </div>
                    <div className="tabular-nums font-semibold">₹{(b.amount || 0).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {trips.map((t) => (
        <Collapsible key={t.id} open={open[t.id]} onOpenChange={(v) => setOpen(o => ({ ...o, [t.id]: v }))}>
          <div className="rounded-2xl bg-white border border-slate-200">
            <div className="p-4 flex items-center justify-between gap-3">
              <CollapsibleTrigger className="text-left flex-1 min-w-0" data-testid={`my-trip-${t.id}`}>
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><ModeIcon m={t.travel_mode}/> {t.travel_mode.toUpperCase()} · {t.date}</div>
                <div className="font-display font-bold truncate">{t.origin} → {t.destination}</div>
                <div className="mt-1 flex flex-wrap gap-2 text-xs">
                  <Badge className="bg-slate-100 text-slate-700">{t.parcel_count} parcel{t.parcel_count !== 1 ? "s" : ""}</Badge>
                  {t.payout_pending > 0 && <Badge className="bg-amber-100 text-amber-800">Paid after delivery · ₹{t.payout_pending.toFixed(0)}</Badge>}
                  <Badge className="bg-emerald-100 text-emerald-700">Earned · ₹{t.gross_earnings.toFixed(0)}</Badge>
                  {t.cancelled && <Badge className="bg-rose-100 text-rose-700">CANCELLED</Badge>}
                </div>
              </CollapsibleTrigger>
              <div className="flex items-center gap-2 shrink-0">
                {canCancel(t) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setCancelling(t); setReason(""); }}
                    data-testid={`cancel-trip-${t.id}`}
                    className="h-8 rounded-full text-[11px] gap-1 border-rose-200 text-rose-700 hover:bg-rose-50"
                  >
                    <XCircle className="w-3.5 h-3.5"/> Cancel trip
                  </Button>
                )}
                <CollapsibleTrigger>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition ${open[t.id] ? "rotate-180" : ""}`}/>
                </CollapsibleTrigger>
              </div>
            </div>
            {t.cancelled && t.cancel_reason && (
              <div className="px-4 pb-3 text-[11px] text-rose-700" data-testid={`trip-cancel-reason-${t.id}`}>
                Cancelled: {t.cancel_reason}
              </div>
            )}
            <CollapsibleContent className="px-4 pb-4 space-y-2 border-t border-slate-100 pt-3">
              {t.deliveries.length === 0 && <div className="text-xs text-slate-400 py-2">No parcels booked yet on this trip.</div>}
              {t.deliveries.map(d => (
                <div key={d.id} className="bg-slate-50 rounded-xl">
                <Link to={`/delivery/${d.id}`} className="flex items-center justify-between hover:bg-slate-100 rounded-xl p-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{d.parcel?.pickup_location} → {d.parcel?.drop_location}</div>
                    <div className="text-xs text-slate-500">{d.sender?.name} · {sizeLabel(d.parcel)}</div>
                  </div>
                  <div className="text-right">
                    <Badge className={STATUS_STYLE[d.status] || "bg-slate-100"}>{d.status.replace("_"," ")}</Badge>
                    <div className="text-sm font-display font-bold mt-1">₹{d.carrier_payout?.toFixed(0)}</div>
                  </div>
                </Link>
                {d.contact?.available && <div className="px-3 pb-3"><ContactButtons delivery={d} role="carrier"/></div>}
                </div>
              ))}
            </CollapsibleContent>
          </div>
        </Collapsible>
      ))}

      {/* Cancel-trip dialog */}
      <Dialog open={!!cancelling} onOpenChange={(v) => { if (!v) { setCancelling(null); setReason(""); } }}>
        <DialogContent data-testid="cancel-trip-dialog">
          <DialogHeader>
            <DialogTitle>Cancel this trip?</DialogTitle>
            <DialogDescription>
              {cancelling && (cancelling.deliveries || []).length > 0 ? (
                <>
                  This trip has <b>{(cancelling.deliveries || []).length}</b> booked parcel{(cancelling.deliveries || []).length > 1 ? "s" : ""}.
                  Affected senders will be notified, their parcels returned to the pool, and any held payment refunded in full.
                </>
              ) : (
                "No senders are booked on this trip yet, so no one will be impacted."
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Reason <span className="text-rose-600">*</span></label>
            <Textarea
              data-testid="cancel-trip-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., my plans changed, vehicle issue, illness"
              rows={3}
              className="mt-1"
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelling(null); setReason(""); }} disabled={busy}>Keep trip</Button>
            <Button
              onClick={submitCancel}
              disabled={busy || !reason.trim()}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="cancel-trip-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Cancel trip"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyTrips;
